import { Box, Chip, Divider, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArticleSourceType,
  ArticleType,
  AttachmentType,
} from '../../../../pages/knowledge/hooks/useArticlesQuery';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import { PostDescription, PostProject, PostTitle } from '@buildwise/libs/posts';
import { ConfigEntryType } from 'config/config';
import { ArticleVariantType } from '@buildwise/pages/knowledge/components/ArticleBox';
import { Dispatch, forwardRef, Fragment, memo, useMemo } from 'react';
import Attachments from '@buildwise/pages/knowledge/components/Attachments';
import RenderDocuments from '@buildwise/pages/article/components/RenderDocuments';
import { UseArticleReducerActions } from '@buildwise/pages/knowledge/hooks/useArticle';
import { ArticleAttachmentsSummary } from '@buildwise/pages/article/hooks/useArticleAttachmentsSummary';
import { UserLink } from '@buildwise/pages/knowledge/components/DraftStatusMessage';
import { theme } from 'config/theme';
import { ReactComponent as Progress3LineIcon } from 'assets/icons/Progress3LineIcon.svg';
import Avatar from 'components/common/avatar/Avatar';
import LightbulbFlashFillIcon from 'remixicon-react/LightbulbFlashFillIcon';
import { USER_PROFILE } from 'constants/routes';
import {
  ArticleTypeLabelContainer,
  ArticleTypeLabel,
} from '@buildwise/pages/knowledge/components/ArticleInteractions';
import ChatQuoteFillIcon from 'remixicon-react/ChatQuoteFillIcon';
import QuestionFillIcon from 'remixicon-react/QuestionFillIcon';
import FileListFillIcon from 'remixicon-react/FileListFillIcon';
import useArticleDetailsQuery from '@buildwise/pages/article/hooks/useArticleDetailsQuery';
import UserAvatar from 'components/common/userAvatar/UserAvatar';
import RenderSuggestionStatus from './RenderSuggestionStatus';

type PostInfoProps = {
  article: ArticleType;
  articleAttachmentsSummary?: ArticleAttachmentsSummary;
  articleFields: ConfigEntryType[];
  documents: AttachmentType[];
  isDetailedView: boolean;
  isRelatedPostView: boolean;
  mediaFiles: AttachmentType[];
  searchKeyword: string;
  variant: ArticleVariantType;
  dispatch: Dispatch<UseArticleReducerActions>;
};

const PostInfo = forwardRef<HTMLParagraphElement, PostInfoProps>(
  (
    {
      article,
      articleAttachmentsSummary,
      articleFields,
      documents,
      isDetailedView,
      isRelatedPostView,
      mediaFiles,
      searchKeyword,
      variant,
      dispatch,
    },
    articleDescriptionRef,
  ) => {
    const { singularProjectsModuleName } = useBuildWiseModules();
    const { articleResponse, isLoading, isError } = useArticleDetailsQuery({
      articleId: article.id,
      updateViewCount: false,
      shouldFetch: false,
    });

    return (
      <>
        <Chip
          label={
            <>
              {article.articleType === ArticleSourceType.QUESTION ? (
                <ArticleTypeLabelContainer>
                  <QuestionFillIcon color={theme.palette.custom.dodgerBlue} size={18} />
                  <ArticleTypeLabel>Question</ArticleTypeLabel>
                </ArticleTypeLabelContainer>
              ) : article.articleType === ArticleSourceType.SUGGESTION ? (
                <ArticleTypeLabelContainer>
                  <ChatQuoteFillIcon color={theme.palette.custom.cloverGreen} size={18} />
                  <ArticleTypeLabel>Suggestion</ArticleTypeLabel>
                </ArticleTypeLabelContainer>
              ) : (
                <ArticleTypeLabelContainer>
                  <FileListFillIcon color={theme.palette.custom.pirateGold} size={18} />
                  <ArticleTypeLabel>Post</ArticleTypeLabel>
                </ArticleTypeLabelContainer>
              )}
            </>
          }
          variant="outlined"
          sx={{
            my: 0.5,
            border: 'none',
            backgroundColor:
              article.articleType === ArticleSourceType.QUESTION
                ? '#c7ecff'
                : article.articleType === ArticleSourceType.SUGGESTION
                  ? '#d2ffde'
                  : theme.palette.custom.yellowBorder,
            '&:hover': {
              backgroundColor:
                article.articleType === ArticleSourceType.QUESTION
                  ? '#c7ecff'
                  : article.articleType === ArticleSourceType.SUGGESTION
                    ? '#d2ffde'
                    : theme.palette.custom.yellowBorder,
            },
          }}
        />
        {articleFields.map((field) => {
          switch (field.entryCode) {
            case 'title': {
              const value =
                article.title ||
                (typeof article.customFields?.['title']?.fieldValue === 'string'
                  ? article.customFields['title']?.fieldValue
                  : '');
              return (
                <PostTitle
                  key={field.entryCode}
                  searchKeyword={searchKeyword}
                  title={value}
                  variant={variant}
                />
              );
            }

            case 'description': {
              const value =
                article.description ||
                (typeof article.customFields?.['description']?.fieldValue === 'string'
                  ? article.customFields['description']?.fieldValue
                  : '');
              return (
                <>
                  <PostDescription
                    key={field.entryCode}
                    description={
                      isDetailedView ? value : value?.slice(0, 5000)?.replaceAll('&nbsp;', '')
                    }
                    isDetailedView={isDetailedView}
                    isRelatedPostView={isRelatedPostView}
                    ref={articleDescriptionRef}
                    searchKeyword={searchKeyword}
                    variant={variant}
                  />
                  {documents.length > 0 && (
                    <RenderDocuments
                      documents={documents}
                      articleAttachmentsSummary={articleAttachmentsSummary}
                    />
                  )}
                </>
              );
            }

            case 'attachments': {
              if (mediaFiles.length > 0) {
                return (
                  <Attachments
                    key={field.entryCode}
                    attachments={mediaFiles}
                    article={article}
                    doNotAutoplayVideos
                    variant={variant}
                    setLightboxOpen={(payload: { open: boolean; fileName: string }) =>
                      dispatch({ type: 'TOGGLE_LIGHTBOX', payload })
                    }
                  />
                );
              }

              return null;
            }

            case 'projectId': {
              if (article.project) {
                return (
                  <Fragment key={field.entryCode}>
                    <Title>{singularProjectsModuleName} Name</Title>
                    <PostProject project={article.project} searchKeyword={searchKeyword} />
                  </Fragment>
                );
              }

              return null;
            }

            // Hiding partners & playlists till the time we get a requirement to display.
            case 'partnerIds':
            case 'playListArticleIds': {
              return null;
            }

            case 'taskOwner': {
              if (article.taskOwner) {
                const userDetails = articleResponse?.userMap?.[article.taskOwner]?.[0];
                return (
                  <Fragment key={field.entryCode}>
                    <Title>{field.entryDescription}</Title>
                    {userDetails ? (
                      <UserAvatar
                        isSmall
                        avatarSize={30}
                        profileImageURL={userDetails.profileImageURL}
                        title={userDetails.displayName}
                        avatarName={userDetails.displayName}
                        userName={userDetails.userName}
                        sx={{ mt: 1 }}
                      />
                    ) : (
                      article.taskOwner
                    )}
                  </Fragment>
                );
              }

              return null;
            }

            case 'taskStatus': {
              if (article.taskStatus) {
                return (
                  <Fragment key={field.entryCode}>
                    <Title>{field.entryDescription}</Title>
                    <RenderSuggestionStatus status={article.taskStatus} />
                  </Fragment>
                );
              }

              return null;
            }

            default: {
              const value =
                article[field.entryCode] || article.customFields?.[field.entryCode]?.fieldValue;
              if (Array.isArray(value) ? value.length : value) {
                return (
                  <Fragment key={field.entryCode}>
                    <Title>{field.entryDescription}</Title>
                    {Array.isArray(value) ? value.join(', ') : value}
                  </Fragment>
                );
              }

              return null;
            }
          }
        })}
      </>
    );
  },
);

export const Title = styled(Typography, { label: 'Title' })({
  fontSize: 14,
  fontWeight: 700,
  marginTop: 12,
});

export default memo(PostInfo);
